import { Box, styled } from '@mui/material'

import type { PageLayoutProps } from './PageLayout.types'

const FALLBACK_NAV_WIDTH = '240px'
const FALLBACK_SIDEBAR_WIDTH = '400px'
const FALLBACK_SIDEBAR_COLLAPSED_WIDTH = '30px'

type PageLayoutRootProps = Pick<
  PageLayoutProps,
  'variant' | 'sidebarOpen' | 'sidebarWidth' | 'sidebarCollapsedWidth'
> & {
  shouldTransition?: boolean
  shouldRenderGhost?: boolean
}

const toCssLength = (value?: number | string) => (typeof value === 'number' ? `${value}px` : value)

export const PageLayoutRoot = styled(Box, {
  name: 'PageLayout',
  slot: 'Root',
  overridesResolver: (_props, styles) => styles.root,
  shouldForwardProp: (prop) =>
    prop !== 'variant' &&
    prop !== 'sidebarOpen' &&
    prop !== 'sidebarWidth' &&
    prop !== 'sidebarCollapsedWidth' &&
    prop !== 'shouldTransition' &&
    prop !== 'shouldRenderGhost',
})<PageLayoutRootProps>(({
  theme,
  variant = 'one-column',
  sidebarOpen,
  sidebarWidth,
  sidebarCollapsedWidth,
  shouldTransition = true,
  shouldRenderGhost,
}) => {
  const navWidth = theme.mixins?.sidebar?.width ? theme.mixins?.sidebar?.width.toString() : FALLBACK_NAV_WIDTH
  const openWidth = toCssLength(sidebarWidth) ?? toCssLength(theme.mixins?.rightSidebar?.width) ?? FALLBACK_SIDEBAR_WIDTH
  const collapsedWidth =
    toCssLength(sidebarCollapsedWidth) ??
    toCssLength(theme.mixins?.rightSidebar?.collapsedWidth) ??
    FALLBACK_SIDEBAR_COLLAPSED_WIDTH

  let width = '100%'

  if (variant === 'two-column') {
    width = `calc(100% - ${navWidth})`
  } else if (variant === 'two-column-with-sidebar') {
    width = `calc(100% - ${navWidth} - ${sidebarOpen ? openWidth : collapsedWidth})`
  }

  return {
    marginTop: shouldRenderGhost ? 0 : theme.spacing(6),
    display: 'flex',
    flexDirection: 'column',
    marginLeft: variant === 'two-column' || variant === 'two-column-with-sidebar' ? navWidth : 'auto',
    flex: 1,
    width,
    maxHeight: '100vh',
    overflowY: 'scroll',
    ...(shouldTransition
      ? {
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }
      : {}),
  }
})
